/**
 * Vista rápida para tarjetas por variante (CyberAliado).
 * - Botones [data-vista-rapida="<url de la variante>"] abren un <dialog>.
 * - El contenido es la sección "vista-rapida" renderizada por Shopify (Section Rendering API).
 * - El botón "Agregar al carrito" usa el product-form-component de Horizon.
 */
const SECTION_ID = 'vista-rapida';
const cache = new Map();
let dialog = null;
let lastTrigger = null;

const closeIcon =
  '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';

function sectionUrl(url) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('section_id', SECTION_ID);
  return u.toString();
}

function fetchSection(url) {
  if (!cache.has(url)) {
    const request = fetch(sectionUrl(url), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .catch((error) => {
        cache.delete(url);
        throw error;
      });
    cache.set(url, request);
  }
  return cache.get(url);
}

function ensureDialog() {
  if (dialog) return dialog;

  dialog = document.createElement('dialog');
  dialog.className = 'vr-dialog';
  dialog.setAttribute('aria-label', 'Vista rápida');
  dialog.innerHTML = `<button type="button" class="vr-dialog__cerrar" aria-label="Cerrar vista rápida">${closeIcon}</button><div class="vr-dialog__contenido"></div>`;
  document.body.appendChild(dialog);

  dialog.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Clic en el fondo (fuera del contenido) o en el botón cerrar
    if (target === dialog || target.closest('.vr-dialog__cerrar')) {
      dialog.close();
      return;
    }

    // Cambiar de color dentro del modal
    const swatch = target.closest('[data-vr-url]');
    if (swatch) {
      event.preventDefault();
      if (swatch.classList.contains('is-activa')) return;
      load(swatch.getAttribute('data-vr-url'));
      return;
    }

    // Cambiar imagen principal
    const mini = target.closest('[data-vr-swap]');
    if (mini) {
      const main = dialog.querySelector('[data-vr-main]');
      if (main) {
        main.setAttribute('srcset', mini.getAttribute('data-vr-srcset') || '');
        main.setAttribute('src', mini.getAttribute('data-vr-swap') || '');
      }
      dialog.querySelectorAll('.vr__mini').forEach((el) => el.classList.toggle('is-activa', el === mini));
    }
  });

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('vr-abierto');
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
  });

  // Cerrar el modal cuando el producto se agrega correctamente al carrito
  import('@shopify/events')
    .then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        if (!dialog?.open || !(event.target instanceof Node) || !dialog.contains(event.target)) return;
        const promise = /** @type {any} */ (event).promise;
        if (!promise) return;
        promise
          .then((result) => {
            if (result?.detail?.didError) return;
            setTimeout(() => dialog.open && dialog.close(), 500);
          })
          .catch(() => {});
      });
    })
    .catch(() => {});

  return dialog;
}

async function load(url) {
  const d = ensureDialog();
  const box = d.querySelector('.vr-dialog__contenido');
  box.setAttribute('aria-busy', 'true');
  d.classList.add('is-cargando');

  try {
    const html = await fetchSection(url);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector('[data-vr-root]');
    if (!root) throw new Error('Vista rápida sin contenido');
    box.replaceChildren(document.importNode(root, true));
  } finally {
    box.removeAttribute('aria-busy');
    d.classList.remove('is-cargando');
  }
}

async function openQuickView(url, trigger) {
  lastTrigger = trigger;
  trigger?.setAttribute('aria-busy', 'true');

  try {
    await load(url);
    const d = ensureDialog();
    if (!d.open) {
      d.showModal();
      document.documentElement.classList.add('vr-abierto');
    }
  } catch (error) {
    // Si algo falla, llevamos al cliente a la ficha del producto
    window.location.href = url;
  } finally {
    trigger?.removeAttribute('aria-busy');
  }
}

// Botones propios de las tarjetas por variante
document.addEventListener('click', (event) => {
  const trigger = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-vista-rapida]');
  if (!trigger) return;

  event.preventDefault();
  const url = trigger.getAttribute('data-vista-rapida');
  if (url) openQuickView(url, trigger);
});

// Botón "Elegir" de Horizon (productos con varias opciones, p. ej. polos con talla):
// su modal nativo depende de la plantilla de producto estándar, así que usamos esta vista rápida.
// Se escucha en window (fase de captura) para adelantarnos al manejador de Horizon.
window.addEventListener(
  'click',
  (event) => {
    const choose = /** @type {HTMLElement} */ (event.target)?.closest?.('.quick-add__button--choose');
    if (!choose) return;

    const card = /** @type {any} */ (choose.closest('product-card'));
    const link = card?.querySelector('[ref="productCardLink"], .product-card__link');
    let url = '';
    try {
      url = card?.productPageUrl || link?.href || '';
    } catch (error) {
      url = link?.href || '';
    }
    if (!url) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openQuickView(url, choose);
  },
  true
);

// Precarga al pasar el mouse para que el modal abra al instante
document.addEventListener(
  'pointerover',
  (event) => {
    const trigger = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-vista-rapida]');
    if (!trigger) return;
    const url = trigger.getAttribute('data-vista-rapida');
    if (url && !cache.has(url)) fetchSection(url).catch(() => {});
  },
  { passive: true }
);
